import { Fragment, ReactNode, useState } from 'react';

import { CheckIcon, ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { PopoverComboboxTrigger } from '@/components/ui/custom/PopoverCombobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface PickerOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** Extra search terms, for values that are ids. */
  keywords?: string[];
}

export interface PickerGroup {
  key: string;
  heading?: string;
  options: PickerOption[];
}

interface PickerComboboxProps {
  groups: PickerGroup[];
  value: string | null;
  onSelect: (value: string) => void;
  /** The trigger's content. */
  children: ReactNode;
  /** The pinned footer link that opens a settings page in a new tab. */
  footer: { to: string; label: string };
  disabled?: boolean;
  error?: boolean;
  className?: string;
  /** Forwarded to PopoverContent. Use to bump z-index above modals (e.g. `z-3000`). */
  contentClassName?: string;
  'aria-label'?: string;
}

/** Searchable, grouped combobox with a check on the current value and a footer link. */
export const PickerCombobox = ({
  groups,
  value,
  onSelect,
  children,
  footer,
  disabled,
  error,
  className,
  contentClassName,
  'aria-label': ariaLabel,
}: PickerComboboxProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const visibleGroups = groups.filter((group) => group.options.length > 0);

  const select = (next: string) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PopoverComboboxTrigger
          open={open}
          error={error}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn('h-10', className)}
        >
          {children}
        </PopoverComboboxTrigger>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Keep the popover clear of the sticky nav (48px) + subheader (56px) when
        // it flips above the trigger, so it never paints over them.
        collisionPadding={{ top: 112, bottom: 16 }}
        className={cn('w-(--radix-popover-trigger-width) p-0', contentClassName)}
      >
        <Command>
          <CommandInput placeholder={t('common.search_dots')} />
          {/* Shrink to the space Radix has (minus the input + sticky footer) instead of a
              fixed height, so a long list doesn't overflow over the header when flipped up. */}
          <CommandList className="max-h-[calc(var(--radix-popover-content-available-height)-5rem)]">
            <CommandEmpty>{t('common.no_results')}</CommandEmpty>
            {visibleGroups.map((group, index) => (
              <Fragment key={group.key}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={group.heading}>
                  {group.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={option.keywords}
                      disabled={option.disabled}
                      onSelect={() => select(option.value)}
                    >
                      {option.icon}
                      <span className="flex-1 truncate">{option.label}</span>
                      {value === option.value && <CheckIcon className="h-4 w-4 shrink-0" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Fragment>
            ))}
          </CommandList>

          {/* Pinned footer — stays visible while the list above scrolls. */}
          <div className="border-t p-1">
            <Link
              to={footer.to}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground hover:bg-highlight-background"
            >
              <PlusIcon className="h-4 w-4 text-foreground-muted" />
              <span className="flex-1">{footer.label}</span>
              <ExternalLinkIcon className="h-3.5 w-3.5 text-foreground-muted" />
            </Link>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
